// Modal: review the Enformion candidates from the last skip-trace
// and pick the correct person. Opens from the ⚠ indicator on a
// lead-list row. The candidate list comes pre-loaded on the lead-
// list payload (no extra round trip).
//
// Each candidate card shows the differentiators that matter for the
// "which one is the right John Smith" judgment: name, age, top
// addresses (most-recent first), phone count, deceased badge. The
// currently-selected candidate (by tahoeId) gets an "✓ current"
// badge so the operator sees what they're switching FROM.
//
// Pick → server call → close → caller reloads the list. Already-on
// case fast-paths to "clear needs_review and close" without a
// merge.

import { useState } from 'react';
import { partyApi } from '../services/partyApi';
import type { SlimCandidate } from '../services/leadListApi';

interface Props {
  partyId: string;
  partyDisplayName: string;
  candidates: SlimCandidate[];
  currentTahoeId: string | null;
  onClose: () => void;
  /** Called after a successful pick. Caller refetches the lead list. */
  onPicked: () => void | Promise<void>;
}

export function SkipTraceCandidatesModal(props: Props) {
  const { partyId, partyDisplayName, candidates, currentTahoeId, onClose, onPicked } = props;
  const [busyTahoe, setBusyTahoe] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function pick(c: SlimCandidate) {
    if (!c.tahoeId) return;
    setBusyTahoe(c.tahoeId);
    setErr(null);
    try {
      await partyApi.skipTracePick(partyId, c.tahoeId);
      await onPicked();
      onClose();
    } catch (e: any) {
      setErr(e?.message ?? 'pick failed');
      setBusyTahoe(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={onClose}>
      <div className="bg-bg-card border border-border rounded-lg w-[860px] max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-border flex items-center justify-between shrink-0">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-text-faint">enformion candidates</div>
            <div className="font-display text-[22px] leading-tight">{partyDisplayName}</div>
            <div className="text-[11px] font-mono text-text-faint mt-1">
              {candidates.length} {candidates.length === 1 ? 'candidate' : 'candidates'} from last trace · pick the right person to merge their phones in
            </div>
          </div>
          <button onClick={onClose} className="text-text-dim hover:text-text text-2xl leading-none">×</button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3">
          {err && (
            <div className="text-[13px] text-red bg-red/10 border border-red/40 rounded px-4 py-3">{err}</div>
          )}
          {candidates.length === 0 && (
            <div className="text-[12px] text-text-faint font-mono text-center py-12">
              No candidates stored. Re-run skip-trace to populate.
            </div>
          )}
          {candidates.map(c => (
            <CandidateCard
              key={c.tahoeId}
              candidate={c}
              isCurrent={!!currentTahoeId && c.tahoeId === currentTahoeId}
              busy={busyTahoe === c.tahoeId}
              disabled={busyTahoe !== null && busyTahoe !== c.tahoeId}
              onPick={() => pick(c)}
            />
          ))}
        </div>

        <div className="px-6 py-3 border-t border-border bg-bg-card-soft/40 shrink-0 flex items-center justify-between">
          <div className="text-[10px] font-mono text-text-faint">
            Picking is free — no Enformion call. Manual phones from prior picks survive; prune in PartyDetail if needed.
          </div>
          <button onClick={onClose} className="px-3 py-1.5 text-[12px] text-text-dim hover:text-text border border-border rounded">Close</button>
        </div>
      </div>
    </div>
  );
}

function CandidateCard(props: {
  candidate: SlimCandidate;
  isCurrent: boolean;
  busy: boolean;
  disabled: boolean;
  onPick: () => void;
}) {
  const { candidate: c, isCurrent, busy, disabled, onPick } = props;
  const fullName = [c.firstName, c.middleName, c.lastName].filter(Boolean).join(' ') || '(no name)';
  // Sort addresses: current first, then by lastReportedDate DESC.
  const addrs = [...c.addresses].sort((a, b) => {
    if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;
    const ad = a.lastReportedDate ?? '';
    const bd = b.lastReportedDate ?? '';
    return bd.localeCompare(ad);
  });
  const phoneCount = c.phoneNumbers.length;
  const connectedCount = c.phoneNumbers.filter(p => p.isConnected === true).length;

  return (
    <div className={
      // Current candidate: subtle left-edge accent + slightly stronger
      // border, but NO background tint. The "✓ CURRENT" pill in the
      // header carries the visual signal; tinting the whole card was
      // washing out the address/phone text against the yellow.
      'rounded p-4 ' +
      (isCurrent
        ? 'border border-accent/40 border-l-2 border-l-accent bg-bg-card-soft'
        : 'border border-border bg-bg-card-soft')
    }>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[14px] text-text font-medium">{fullName}</div>
            {c.age !== null && (
              <span className="text-[11px] font-mono text-text-faint">age {c.age}</span>
            )}
            {c.deceased && (
              <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border border-red/40 bg-red/10 text-red">
                Deceased
              </span>
            )}
            {isCurrent && (
              <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border border-accent/50 bg-accent-soft text-accent">
                ✓ current
              </span>
            )}
          </div>
          {/* tahoeId intentionally not surfaced — opaque to the operator;
              still stored + used for the pick endpoint's lookup. */}

          <div className="grid grid-cols-2 gap-x-6 gap-y-1 mt-3 text-[11px]">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mb-1">
                Addresses ({addrs.length})
              </div>
              {addrs.length === 0
                ? <div className="text-text-ghost italic font-mono">—</div>
                : addrs.slice(0, 4).map((a, i) => (
                    <div key={i} className={a.isCurrent ? 'text-text' : 'text-text-dim'}>
                      <span className="font-mono">
                        {[a.line1, a.city, a.state, a.zip].filter(Boolean).join(', ') || '(empty)'}
                      </span>
                      {a.isCurrent && <span className="text-green ml-1.5 text-[10px]">●</span>}
                      {a.lastReportedDate && <span className="text-text-faint ml-1.5 text-[10px] font-mono">{a.lastReportedDate}</span>}
                    </div>
                  ))
              }
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-mono text-text-faint mb-1">
                Phones ({phoneCount} total · {connectedCount} connected)
              </div>
              {phoneCount === 0
                ? <div className="text-text-ghost italic font-mono">—</div>
                : c.phoneNumbers.slice(0, 4).map((p, i) => (
                    <div key={i} className="text-text-dim font-mono">
                      {p.phoneNumber}
                      {p.phoneType && <span className="text-text-faint ml-1.5 text-[10px]">{p.phoneType}</span>}
                      {p.isConnected === true  && <span className="text-green   ml-1.5 text-[10px]">●</span>}
                      {p.isConnected === false && <span className="text-text-ghost ml-1.5 text-[10px]">○</span>}
                    </div>
                  ))
              }
            </div>
          </div>
        </div>

        <div className="shrink-0">
          <button onClick={onPick}
            disabled={busy || disabled || !c.tahoeId}
            className={
              'px-3 py-1.5 text-[12px] font-medium rounded ' +
              (isCurrent
                ? 'border border-accent/40 text-accent hover:bg-accent-soft'
                : 'bg-accent text-bg hover:bg-accent-bright') +
              ' disabled:opacity-40 disabled:cursor-not-allowed'
            }>
            {busy ? 'Picking…' : isCurrent ? 'Confirm this one' : 'Pick this one'}
          </button>
        </div>
      </div>
    </div>
  );
}
